import React, { useContext } from 'react';
import {
  ButtonVariant, ButtonSize, IconName, Tooltip,
} from '@usealloy/component-library';
import { StyledSwitchButton } from '../styles';
import { switchThresholdToDynamicComparison, switchThresholdToStaticComparison } from 'flow/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectNodeContainerState } from 'flow/store/redux/selectors';
import { ThresholdContext } from './context';
import { RuleBuilderContext } from '../../context';
import { UserContext } from '~/dashboard/context';
import { isPreviewAction } from 'flow/service/actions';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { makeUiEffectContext } from 'flow/service/history/test-utils';
import { Operation } from 'flow/service/changelog/types';

const DynamicSwitchButton = () => {
  const dispatch = useAppDispatch();
  const nodeContainerState = useAppSelector(selectNodeContainerState);

  const { isDynamic, populatedThreshold } = useContext(ThresholdContext);
  const { featureFlags } = useContext(UserContext);
  const { sourceAction } = useContext(RuleBuilderContext);

  const handleDispatch = (stateChange: WorkflowStateChange) => {
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo) && !isPreviewAction(sourceAction),
      {
        operation: Operation.Modify,
        uiEffectContext: makeUiEffectContext(changeDescription.threshold, {
          actionId: populatedThreshold.actionId,
          publicId: populatedThreshold.publicId,
        }),
      },
    ));
  };

  const onSwitchToDynamic = () => handleDispatch(
    () => dispatch(switchThresholdToDynamicComparison({
      thresholdId: populatedThreshold.publicId,
      nodeContainerState,
    })),
  );

  const onSwitchToStatic = () => handleDispatch(
    () => dispatch(switchThresholdToStaticComparison({
      thresholdId: populatedThreshold.publicId,
    })),
  );

  const tooltipContent = isDynamic ? 'Switch to static comparison' : 'Switch to dynamic comparison';

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      delayDuration={1000}
      side="top"
      variant="primary"
      hide={!tooltipContent}
    >
      <StyledSwitchButton
        variant={ButtonVariant.tertiary}
        size={ButtonSize.small}
        onClick={isDynamic ? onSwitchToStatic : onSwitchToDynamic}
        icon={IconName.Rerun}
        aria-label={tooltipContent}
      />
    </Tooltip>
  );
};

export default DynamicSwitchButton;
